/**
 * @file  footer/index.ts
 * @description  application footer component
 *
 * @author ogarcia (ozkary)
 *
 */
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Row } from 'antd';
import * as _datetime from '../../services/datetime';
import { getPhoneNumber } from '../../services/settings';
import { Column } from '../../elements/Column';
import PhoneLink from '../../elements/PhoneLink';
import If from '../../elements/If';
import Image from '../../elements/Image';
import HelpTerms from '../HelpTerms';
import HelpTermsUse from '../HelpTermsUse';
import { ContentLoader } from './content';
import { FooterModel } from './footer.model';
import * as S from './styles';
import './styles.scss';

const Footer = (): JSX.Element => {
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const [showTermsUse, setShowTermsUse] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>('');
  const year = (_datetime.now() as Date).getFullYear();
  const [Content, setContent] = useState<FooterModel>({} as FooterModel);

  const loadContent = async (): Promise<void> => {
    const content = (await ContentLoader);
    setContent(content);
  };

  const loadDocument = async (): Promise<void> => {
    const phone = await getPhoneNumber();
    setPhoneNumber(phone);
  };

  useEffect(() => {
    loadContent();
    loadDocument();
  }, []);

  const phone = (phoneNumber || Content.phone) ?? '';

  const showPrivacy = useCallback((e: React.MouseEvent) => {
    e?.nativeEvent.preventDefault();
    if (Content.privacyPolicyUrl) {
      window.open(Content.privacyPolicyUrl);
    } else {
      setShowHelp(true);
    }

    return false; // prevent postback and scroll to top
  }, [Content]);

  const showTerms = useCallback((e: React.MouseEvent) => {
    e?.nativeEvent.preventDefault();
    if (Content.termUseUrl) {
      window.open(Content.termUseUrl);
    } else {
      setShowTermsUse(true);
    }

    return false; // prevent postback and scroll to top
  }, [Content]);

  return (
    <Fragment>
      <If isTrue={phone !== ''}>
        <Row justify="center" className="need-help">
          <Column span={24}>
            <>
              <PhoneLink
                title={Content.needHelp}
                phone={phone}
                id="phone-number"
              />
            </>
          </Column>
        </Row>
      </If>
      <Row justify="center" className="logo">
        <Column span={24}>
          <Image src={Content.image} title={Content.title} width="200" />
        </Column>
      </Row>
      <Row justify="center" className="privacy">
        <Column span={24}>
          <>
            <S.A href={'#'} onClick={(e) => showPrivacy(e)} title={Content.privacy} aria-label={Content.privacy}>
              {Content.privacy}
            </S.A>
            <S.A href={'#'} onClick={(e) => showTerms(e)} id="termsOfUse" title={Content.termsUse} aria-label={Content.termsUse}>
              {' | '}
              {Content.termsUse}
            </S.A>
          </>
        </Column>
      </Row>
      <Row justify="center" className="copyright">
        <Column span={24}>
          <label>
            {Content.copyright} &copy; {year} {Content.title}
          </label>
        </Column>
      </Row>
      <HelpTerms visible={showHelp} onCancel={() => setShowHelp(false)} />
      <HelpTermsUse
        visible={showTermsUse}
        onCancel={() => setShowTermsUse(false)}
      />
    </Fragment>
  );
};

export default Footer;
